import { getDashboardInsightQueryOptions } from '@/entities/facility';
import type {
  DashboardInsightTab,
  InsightBlock,
} from '@/shared/api/modules/facilities';
import { Icon } from '@/shared/assets/icons';
import { cn } from '@/shared/lib/cn';
import { Button, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { type FC, useEffect, useRef } from 'react';
import { useSettledValue } from '../lib/use-settled-value';
import { useAiInsightPanelStore } from '../model/panel.store';
import { AiInsightEdgeTab, EDGE_TAB_HEIGHT } from './ai-insight-edge-tab';
import { InsightCard } from './insight-card';
import { InsightNextCardSkeleton, InsightSkeleton } from './insight-skeleton';

type AiInsightPanelProps = {
  activeDashboard: DashboardInsightTab;
  dateRange: { from: Dayjs; to: Dayjs };
  /** Omit in Portfolio Mode; a single id scopes the insight to one facility. */
  facilitiesIds?: number[];
  /** Facility name, or "All facilities" — used only for the timing log. */
  scopeLabel?: string;
};

/**
 * The header is drawn 325 x 68 in Figma, but the height is fixed, not scaled
 * with the panel's width — 68 is also exactly `single-facility-tabs.tsx`'s tab
 * strip (48px) plus its gap-5 to the dashboard row below (20px). That sum is
 * constant regardless of viewport, so a fixed 68px header always ends flush
 * with the top of the KPI row beside it, the same line the tab strip already
 * lines up with.
 *
 * This used to scale with panel width (448/4.78 ≈ 94px at 1920) for a closer
 * match to Figma's 4.78:1 proportion at wide viewports. That broke the row
 * alignment above instead — at 1920 the header ran 26px past the KPI row's
 * top, over into the row itself (Anna, ST #182 comment #427: "reduce the
 * height of the blue bar ... so it lines up with the KPI cards"). The row
 * alignment wins; do not reintroduce width-based scaling without also
 * re-deriving it from the tab strip's actual height rather than a ratio.
 */
const HEADER_HEIGHT = 68;

/**
 * Gap between the header's bottom edge and the top of the edge tab.
 *
 * Figma node 244:19890 puts the tab at y=228 against a header ending at y=174,
 * so the tab hangs level with the summary paragraph rather than flush under the
 * band. Anna's 2026-09-15 note asked for the tab "below the header"; we read
 * that as flush and lost the designed offset.
 */
const TAB_OFFSET_BELOW_HEADER = 54;

/**
 * Height of the collapsed panel's box: from its top to the bottom of the tab
 * that hangs off it.
 *
 * The tab is positioned inside the box but is not clipped by it, so a box
 * shorter than this lets the tab stick out below. The box releases its sticky
 * at the end of the dashboard row — and a box capped at the header's height
 * (68px) released with the tab still ~190px below the row, over the top of the
 * facilities table (Anna, ST #182 comment #440: "it gets stuck inside the
 * table ... it should not go below" the insight). Sized to contain the tab, it
 * leaves with the row like the open panel does.
 */
const COLLAPSED_HEIGHT =
  HEADER_HEIGHT + TAB_OFFSET_BELOW_HEADER + EDGE_TAB_HEIGHT;

/**
 * The AI Insight panel. Figma nodes 139-56998 / 139-66155 / 139-87528.
 *
 * Always mounted while the Operational or Financial tab is showing — only its
 * width animates. Unmounting it would take the edge tab with it and would
 * remount the dashboard widgets beside it.
 */
export const AiInsightPanel: FC<AiInsightPanelProps> = ({
  activeDashboard,
  dateRange,
  facilitiesIds,
  scopeLabel,
}) => {
  const isOpen = useAiInsightPanelStore((state) => state.isOpen);
  const toggle = useAiInsightPanelStore((state) => state.toggle);

  // Coalesced so switching tab — which also re-bases the date range — costs one
  // generation rather than two.
  const scope = useSettledValue({
    activeDashboard,
    from: dateRange.from.format('YYYY-MM-DD'),
    to: dateRange.to.format('YYYY-MM-DD'),
    facilitiesIds,
  });

  /*
   * Fetched whether or not the panel is open. The panel mounts with the
   * Operational or Financial tab, so this starts generating as the dashboard
   * loads, and by the time someone opens the panel the answer is usually
   * already there (Alex, 2026-09-22: 15-20s to open is far too long). The
   * server caches each scope for 24h, so this costs one generation per scope
   * per day, not one per visit.
   */
  const { data, isPending, isError, refetch, isFetching } = useQuery(
    getDashboardInsightQueryOptions({ ...scope, enabled: true })
  );

  // Nothing to show yet. Once the first line arrives the real content takes
  // over and only a trailing placeholder remains while the rest streams.
  const isGenerating = isPending && isFetching;
  const isStreaming = Boolean(data && !data.complete && isFetching);

  /*
   * How long the panel took to fill, logged against the scope it was for.
   * Alex timed it at 15-20s on staging (2026-09-22) and wants it under 5s, so
   * the number needs to be visible to anyone who opens the console rather than
   * only to whoever is running a timing script.
   *
   * Two numbers since the insight streams: when the first card appeared (what
   * the user feels) and when the last one did.
   *
   * Measured here rather than inside the query so the query key stays free of a
   * display string — keying on the facility name would discard a generation we
   * already have the moment that name resolves a tick later.
   */
  const fetchStartedAt = useRef<number | null>(null);
  const firstCardSeconds = useRef<string | null>(null);

  if (isFetching && fetchStartedAt.current === null) {
    fetchStartedAt.current = performance.now();
    firstCardSeconds.current = null;
  }

  const secondsSinceFetchStarted = () =>
    ((performance.now() - (fetchStartedAt.current ?? 0)) / 1000).toFixed(1);

  if (
    fetchStartedAt.current !== null &&
    firstCardSeconds.current === null &&
    data?.blocks.length
  ) {
    firstCardSeconds.current = secondsSinceFetchStarted();
  }

  useEffect(() => {
    if (isFetching || fetchStartedAt.current === null) return;

    const seconds = secondsSinceFetchStarted();
    const firstCard = firstCardSeconds.current;
    fetchStartedAt.current = null;
    firstCardSeconds.current = null;

    // eslint-disable-next-line no-console
    console.log(
      `${scopeLabel ?? 'All facilities'} - AI loading time: ${seconds}s` +
        (firstCard && !data?.cached ? `, first card at ${firstCard}s` : '') +
        ` (${activeDashboard.toLowerCase()}${data?.cached ? ', cached' : ''}` +
        `${isOpen ? '' : ', prefetched with panel closed'})`
    );
    // Logs once per fetch, when it settles — not on every open/close.
  }, [isFetching]);

  return (
    /*
     * 26% of the content row, not of the viewport: a vw-based width has to
     * subtract the sidebar itself, and 26vw is 374px at 1440 — well past the
     * 325px Figma draws.
     *
     * The min is 313, not the drawn 325, because the three requirements
     * over-determine the layout at exactly 1440: sidebar 180 + gutter 16 +
     * content 900 + tab 31 + panel 325 = 1452, i.e. 12px more than the
     * viewport. The mock resolves this by having no page gutter at all. We
     * keep the gutter, hold the 900px content floor, and let the panel shrink
     * those 12px — 313/1244 is 25.2%, still inside the required 25-30% band.
     * Above ~1470px it reaches its designed 325 and keeps growing.
     *
     * This has to be a direct child of the content row for that percentage to
     * resolve against the row. One level deeper it resolves against the
     * wrapper's own shrink-to-fit width and the panel never grows past its min.
     */
    <div
      data-testid="ai-insight-panel"
      aria-hidden={!isOpen}
      data-generating={isGenerating || undefined}
      /*
       * Collapsed, the box is only as tall as the tab hanging off it. The
       * insight is prefetched, so a closed panel usually has content, and a
       * full-height box would make the sticky box far taller than anything it
       * shows. The content still overflows visibly, so it stays on screen as
       * the panel slides shut.
       */
      style={isOpen ? undefined : { maxHeight: COLLAPSED_HEIGHT }}
      className={cn(
        // Capped to the viewport so a long insight scrolls inside the panel
        // rather than pushing its own bottom off-screen. Sticky releases at the
        // end of the dashboard row, which is what keeps the panel above the
        // facilities table — the table is the row's sibling, not its child.
        'sticky top-4 max-h-[calc(100vh-8rem)] self-start',
        // No `relative` here: `sticky` already establishes the containing block
        // the edge tab positions against, and the two are the same utility
        // group — `cn()` would merge them and drop the sticky behavior.
        // Clipping lives on the inner box, so it cannot swallow the tab.
        //
        // The tab is positioned rather than laid out, so it no longer claims
        // width in the row the way a flex item did. The margin claims those
        // 31px back — without it the dashboard would widen underneath the tab
        // and the tab would sit on top of the right-hand cards.
        //
        // Height and min-width animate with the width. The collapsed box is
        // capped at the tab's height, so without max-height it snaps to full
        // height the moment the panel opens. And a fixed min-width clamps the
        // box at 313px on the first frame, so the width animation is hidden
        // until its very end, when the panel suddenly widens.
        'ms-[31px] transition-[width,min-width,max-height] duration-300 ease-in-out',
        'motion-reduce:transition-none',
        // While generating there is no content to size the panel, so it would
        // otherwise be a short stub floating beside a full-height dashboard.
        // Holding the full height also means the answer lands in a panel that
        // is already the right size, instead of one that jumps.
        isOpen && isGenerating && 'h-[calc(100vh-8rem)]',
        isOpen ? 'w-[26%] max-w-[480px] min-w-[313px]' : 'w-0 min-w-0'
      )}
    >
      {/*
       * Inside the sticky box, not beside it.
       *
       * As siblings the tab and the panel were each sticky at a matching
       * offset, which looks identical until the page is scrolled far enough:
       * sticky releases when an element's bottom reaches its container's, so a
       * 136px tab stays pinned long after an 872px panel has let go, and the
       * two drift apart — measured at 68px, 222px, 522px, 736px down the page.
       * A child cannot drift from its own parent.
       */}
      <AiInsightEdgeTab
        isOpen={isOpen}
        topOffset={HEADER_HEIGHT + TAB_OFFSET_BELOW_HEADER}
        onClick={toggle}
      />

      {/*
       * The clip lives here rather than on the sticky box: the tab is
       * positioned outside the panel's left edge and would be cut off by an
       * `overflow-hidden` parent. Held at the open width so the content does
       * not reflow as it slides.
       */}
      <div className="h-full max-h-[calc(100vh-8rem)] w-full overflow-hidden">
        <div className="bg-neutral-1 flex h-full max-h-[calc(100vh-8rem)] w-full min-w-[313px] flex-col">
          {/*
           * Gradient, not a flat fill: Figma node 244:19897 is
           * `linear-gradient(-90deg, #1C60CD 0%, #0E3067 100%)` — dark on the
           * left, brighter on the right. Both stops already exist as tokens.
           *
           * `/srgb` because Tailwind v4 interpolates in oklab by default, which
           * bends the midtones away from what Figma renders. The end stops match
           * either way; this makes the middle match too.
           *
           * The logo belongs here and the stars on the side tab (Anna,
           * 2026-09-21). The banner's asset in node 244:19899 is the same
           * artwork as our `ai-summary` icon, path for path.
           */}
          <div
            data-testid="ai-insight-header"
            className="from-primary-8 to-primary-light flex shrink-0 items-center gap-1 rounded-tl-[13px] bg-linear-to-r/srgb px-6"
            style={{ height: HEADER_HEIGHT }}
          >
            <Icon name="ai-summary" size={32} />
            <Typography
              as="h2"
              variant="h2"
              weight="medium"
              className="text-primary-inverse"
            >
              AI Insight
            </Typography>
          </div>

          {/* min-h-0 lets this shrink inside the stretched column so its own
              scrollbar engages instead of the cards overflowing the panel. */}
          <div className="bg-neutral-1 flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">
            {isGenerating && <InsightSkeleton />}

            {isError && !data?.blocks.length && (
              <div className="flex flex-col items-start gap-3 px-6 py-6">
                <Typography className="text-neutral-8">
                  We couldn&apos;t generate an insight for this selection.
                </Typography>
                <Button variant="secondary" onClick={() => refetch()}>
                  Try again
                </Button>
              </div>
            )}

            {data && (
              <>
                {data.summary && (
                  <Typography as="p" className="text-neutral-12 px-6 py-4">
                    {data.summary}
                  </Typography>
                )}

                <div className="flex flex-col gap-5 ps-2">
                  {data.blocks.map((block: InsightBlock) => (
                    <InsightCard block={block} key={block.id} />
                  ))}
                  {isStreaming && <InsightNextCardSkeleton />}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
